import React from 'react';
import styled from '@emotion/styled';
import {
  AbsenceProps,
  CalendarPeriodProps,
  HolidayProps,
  WarningsProps,
} from '../types';
import FormattedDate from '../../../../../../components/date/FormattedDate';
import DurationFormat from '../../../DurationFormat';
import HolidaysAndAbsences from './HolidaysAndAbsences';
import WarningIcon from '../WaningIcon';
import classNames from 'classnames';
import { DayInfo } from '../../../../pages/TimeTracking/TimeTrackingEntries/logic';
import { Day } from '../../../../pages/Projects/types';
import TimesheetDayCommentsPopover from '../../../../pages/Projects/ProjectsTimesheets/TimesheetDayCommentsPopover';
import FlairIcon from '../../../../../../atomic/atoms/FlairIcon';
import { useNamespacedTranslation } from '../../../../../../hooks/useNamespacedTranslation';
import { i18prefix } from '../../../../pages/Projects/helpers';

type Props = {
  dayInfo: DayInfo | Day;
  dayMinutes: number;
  trackedMinutes?: number;
  holidays: HolidayProps[];
  absences: AbsenceProps[];
  calendarPeriods: CalendarPeriodProps[];
  warnings?: WarningsProps | null;
  commentsCount: number;
  showProjectDistribution: boolean;
  isManagerMode?: boolean;
};

const DayHeaderStyled = styled('div')({
  display: 'flex',
  justifyContent: 'space-between',
  marginBottom: 10,
});

const DayHeaderLeft = styled('div')({
  display: 'flex',
  alignItems: 'center',
});

const DayHeaderRight = styled('div')({
  display: 'flex',
  alignItems: 'center',
});

const DayTitle: React.FC = ({ children }) => (
  <div className={classNames('fw-bold')}>{children}</div>
);

const BadgesContainer = styled.div({
  display: 'flex',
  margin: '-10px 0 10px 0',
});

const WarningContainer = styled('div')({
  minWidth: 20,
  textAlign: 'center',
});

const DayHeader: React.FC<Props> = ({
  dayInfo,
  dayMinutes,
  trackedMinutes = 0,
  holidays,
  absences,
  calendarPeriods,
  warnings,
  showProjectDistribution = false,
  isManagerMode = false,
}) => {
  const t = useNamespacedTranslation(i18prefix);
  const remainingMinutes = Math.max(trackedMinutes - dayMinutes, 0);

  return (
    <>
      <DayHeaderStyled>
        <DayHeaderLeft>
          <DayTitle>
            <FormattedDate day={dayInfo.day} format="dayname" />
            &nbsp;-&nbsp;
            <FormattedDate day={dayInfo.day} format="short" />
          </DayTitle>
          <div className="ms-3">
            <TimesheetDayCommentsPopover
              timesheetDayId={dayInfo.timeSheetDayId}
              commentsCount={dayInfo.commentsCount}
            />
          </div>
        </DayHeaderLeft>
        <DayHeaderRight>
          {showProjectDistribution && (
            <>
              <div>
                {t('projectTable.timeTracking')}{' '}
                <span className="fw-bold">
                  <DurationFormat minutes={trackedMinutes} />
                </span>
              </div>
              <FlairIcon
                icon="ellipse-filled"
                width="0.25rem"
                color="var(--bs-flair-gray-2)"
                className="align-middle ms-2 me-2"
              />
            </>
          )}

          {showProjectDistribution && (
            <div>
              {t('projectTable.projects')}
              <span className="fw-bold ms-2">
                <DurationFormat minutes={dayMinutes} />
              </span>
              <span className="ms-2 text-muted">
                ({t('projectTable.remaining')}{' '}
                <span className="fw-bold">
                  <DurationFormat minutes={remainingMinutes} />
                </span>
                )
              </span>
            </div>
          )}
          {!showProjectDistribution && (
            <div>
              <DurationFormat minutes={dayMinutes} />
            </div>
          )}
          <WarningContainer>
            {warnings && (
              <div className="d-inline-block">
                <WarningIcon warnings={warnings} />
              </div>
            )}
          </WarningContainer>
        </DayHeaderRight>
      </DayHeaderStyled>
      {(holidays.length > 0 ||
        absences.length > 0 ||
        calendarPeriods.length > 0) && (
        <BadgesContainer>
          <HolidaysAndAbsences
            holidays={holidays}
            absences={absences}
            calendarPeriods={calendarPeriods}
          />
        </BadgesContainer>
      )}
    </>
  );
};

export default DayHeader;
